import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button, Form, Input, Spin } from 'antd';
import { ApiError } from '../../shared/api/client.ts';
import { useAuth } from './AuthContext.tsx';
import { clearLoggedOut, ssoStatusRequest, wasLoggedOut } from './api.ts';
import styles from './LoginPage.module.css';
import { useDocumentTitle } from '../../shared/hooks/useDocumentTitle.ts';

/** Расшифровка кода ошибки SSO из `?sso_error=`. */
function ssoErrorMessage(code: string): string {
  switch (code) {
    case 'no_access':
      return 'Нет доступа в Lens: у вашей учётной записи нет нужной роли. Обратитесь к администратору.';
    case 'disabled':
      return 'Вход через AlfaProject сейчас отключён.';
    case 'user_inactive':
      return 'Учётная запись отключена.';
    case 'sub_conflict':
      return 'Конфликт учётных записей — обратитесь к администратору.';
    case 'no_provision':
      return 'Учётная запись не заведена в Lens — обратитесь к администратору.';
    case 'state_mismatch':
      return 'Сессия входа истекла, попробуйте ещё раз.';
    case 'token_error':
    case 'no_profile':
    case 'unavailable':
      return 'Не удалось войти через AlfaProject. Попробуйте позже.';
    default:
      return 'Не удалось войти через AlfaProject.';
  }
}

/**
 * Страница входа `/login` (`docs/modules/auth.md §3.4`, §8.5).
 *
 *  - Если вход через AlfaProject включён, страница сразу уводит туда —
 *    формы пароля пользователь не видит. Сам не уводим, когда вернулись
 *    с ошибкой SSO (иначе зациклимся) и когда человек только что нажал
 *    «Выйти» (сессия Keycloak жива — впустило бы обратно): тогда
 *    показываем причину и кнопку.
 *  - Форма пароля — только по `/login?local` (break-glass админ, локальные
 *    учётки) или когда SSO выключен.
 *
 *  - Zod-схема на клиенте намеренно не используется: сервер всё равно
 *    сам валидирует `LoginRequestSchema`, а дубликат политики длины
 *    в UI противоречит §5.2 (не раскрываем структуру пароля).
 *  - На успешный логин редиректим либо в `location.state.from`
 *    (куда пользователя завернул ProtectedRoute), либо на `/`.
 *  - Алерт вместо Form.Item.help, потому что ошибка общая
 *    (не на конкретное поле) и должна быть громкой.
 */

interface LoginFormValues {
  login: string;
  password: string;
}

interface LocationState {
  // Возвращаться нужно на полный адрес: у страницы привязки Telegram в
  // query лежат подписанные данные, и без них она бесполезна.
  from?: { pathname: string; search?: string; hash?: string };
}

function getErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'invalid_credentials':
        return 'Неверный логин или пароль';
      case 'use_sso':
        return 'Этот аккаунт входит через AlfaProject';
      case 'rate_limited':
        return 'Слишком много попыток. Попробуйте позже.';
      case 'validation_error':
        return 'Заполните логин и пароль';
      default:
        return err.message || 'Не удалось войти';
    }
  }
  return 'Не удалось войти. Проверьте соединение.';
}

/** Старт входа через AlfaProject — полная навигация браузера, не XHR. */
function startSso(returnTo: string): void {
  clearLoggedOut();
  const query = returnTo === '/' ? '' : `?returnTo=${encodeURIComponent(returnTo)}`;
  window.location.assign(`/api/auth/sso/login${query}`);
}

/** Полный адрес возврата после входа — вместе с query и hash. */
function returnPath(state: LocationState | null): string {
  const from = state?.from;
  if (!from?.pathname) return '/';
  return `${from.pathname}${from.search ?? ''}${from.hash ?? ''}`;
}

export function LoginPage(): JSX.Element {
  useDocumentTitle('Вход');
  const { status, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // null — статус SSO ещё не пришёл.
  const [ssoEnabled, setSsoEnabled] = useState<boolean | null>(null);
  const [loggedOut] = useState(wasLoggedOut);

  const params = new URLSearchParams(location.search);
  const localMode = params.has('local');
  const ssoErrorCode = params.get('sso_error');
  const ssoError = ssoErrorCode ? ssoErrorMessage(ssoErrorCode) : null;
  const target = returnPath(location.state as LocationState | null);

  useEffect(() => {
    void ssoStatusRequest()
      .then((s) => setSsoEnabled(s.enabled))
      .catch(() => setSsoEnabled(false));
  }, []);

  const autoSso =
    status === 'anonymous' && ssoEnabled === true && !localMode && !ssoError && !loggedOut;

  useEffect(() => {
    if (autoSso) startSso(target);
  }, [autoSso, target]);

  if (status === 'authenticated') {
    return <Navigate to={target} replace />;
  }

  // Ждём статус SSO или уже уходим в AlfaProject — форму не мигаем.
  if (status === 'loading' || ssoEnabled === null || autoSso) {
    return (
      <LoginShell>
        <div className={styles.redirecting} data-testid="sso-redirecting">
          <Spin />
          {autoSso ? <span>Переходим ко входу через AlfaProject…</span> : null}
        </div>
      </LoginShell>
    );
  }

  // SSO включён, но сами не ушли: ошибка входа или человек только что вышел.
  if (ssoEnabled && !localMode) {
    return (
      <LoginShell
        title={ssoError ? 'Не получилось войти' : 'Вы вышли из Lens'}
        lead={
          ssoError ? null : (
            <span data-testid="logged-out-note">
              Чтобы вернуться, войдите через AlfaProject.
            </span>
          )
        }
      >
        {ssoError ? (
          <Alert
            type="error"
            message={ssoError}
            showIcon
            className={styles.alert}
            data-testid="sso-error"
          />
        ) : null}
        <Button
          type="primary"
          block
          size="large"
          onClick={() => startSso(target)}
          data-testid="sso-login"
        >
          Войти через AlfaProject
        </Button>
      </LoginShell>
    );
  }

  const onFinish = async (values: LoginFormValues): Promise<void> => {
    setSubmitting(true);
    setErrorMessage(null);
    try {
      await login({ login: values.login, password: values.password });
      navigate(target, { replace: true });
    } catch (err) {
      setErrorMessage(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LoginShell
      title="Вход по паролю"
      lead={ssoEnabled ? 'Для запасного администратора и локальных учётных записей.' : null}
    >
      {errorMessage ? (
        <Alert
          type="error"
          message={errorMessage}
          showIcon
          className={styles.alert}
          data-testid="login-error"
        />
      ) : null}

      {ssoError ? (
        <Alert
          type="error"
          message={ssoError}
          showIcon
          className={styles.alert}
          data-testid="sso-error"
        />
      ) : null}

      <Form<LoginFormValues>
        layout="vertical"
        onFinish={onFinish}
        requiredMark={false}
        disabled={submitting}
        size="large"
      >
        <Form.Item
          label="Логин"
          name="login"
          rules={[{ required: true, message: 'Укажите логин' }]}
        >
          <Input autoFocus autoComplete="username" data-testid="login-input" />
        </Form.Item>
        <Form.Item
          label="Пароль"
          name="password"
          rules={[{ required: true, message: 'Введите пароль' }]}
        >
          <Input.Password autoComplete="current-password" data-testid="password-input" />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
          <Button
            type="primary"
            htmlType="submit"
            block
            size="large"
            loading={submitting}
            data-testid="login-submit"
          >
            {submitting ? 'Вход…' : 'Войти'}
          </Button>
        </Form.Item>
      </Form>

      {ssoEnabled ? (
        <Button
          type="link"
          className={styles.altLink}
          onClick={() => startSso(target)}
          data-testid="sso-login"
        >
          Войти через AlfaProject
        </Button>
      ) : null}
    </LoginShell>
  );
}

interface LoginShellProps {
  title?: string;
  lead?: React.ReactNode;
  children: React.ReactNode;
}

/** Раскладка экрана входа: слева панель с Ленси, справа содержимое. */
function LoginShell({ title, lead, children }: LoginShellProps): JSX.Element {
  return (
    <div className={styles.page} data-testid="login-page">
      <aside className={styles.panel} aria-hidden="true">
        <div className={styles.portrait}>
          <img src="/lensy.jpg" alt="" className={styles.mascot} data-testid="login-mascot" />
        </div>
        <p className={styles.panelCaption}>
          Квартальные цели, техинициативы и&nbsp;задачи команд — в&nbsp;одном месте.
        </p>
      </aside>

      <main className={styles.content}>
        <div className={styles.column}>
          <div className={styles.brand}>
            <img src="/favicon.svg" alt="" className={styles.logo} data-testid="login-logo" />
            <span className={styles.wordmark}>Lens</span>
          </div>
          {title ? <h1 className={styles.title}>{title}</h1> : null}
          {lead ? <p className={styles.lead}>{lead}</p> : null}
          {children}
        </div>
      </main>
    </div>
  );
}
